import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, Modal, Row, Spinner, Tab, Table, Tabs } from 'react-bootstrap';
import axios from '../utils/api';

const AdminOrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [actionLoading, setActionLoading] = useState('');
  const [courierLinkInfo, setCourierLinkInfo] = useState(null);
  const [actionError, setActionError] = useState('');
  const [actionSuccess, setActionSuccess] = useState('');
  const [showTxModal, setShowTxModal] = useState(false);
  const [txRows, setTxRows] = useState([]);
  const [txLoading, setTxLoading] = useState(false);
  const [txError, setTxError] = useState('');
  const [txOrderLabel, setTxOrderLabel] = useState('');
  const [shippingDialog, setShippingDialog] = useState({
    show: false,
    orderId: '',
    trackingNumber: '',
    courierCompany: '',
    dateShipped: '',
  });
  const [deliveryDialog, setDeliveryDialog] = useState({
    show: false,
    orderId: '',
    deliveryDate: '',
    deliveredTo: '',
  });
  const [returnPickupDialog, setReturnPickupDialog] = useState({
    show: false,
    order: null,
    pickupDate: '',
    pickupNote: '',
    itemChecks: {},
  });
  const [returnReceivedDialog, setReturnReceivedDialog] = useState({
    show: false,
    order: null,
    receivedDate: '',
    note: '',
    orderRecommendation: 'refund',
    itemRows: [],
  });
  const [cancelDialog, setCancelDialog] = useState({
    show: false,
    mode: 'order',
    order: null,
    item: null,
    reason: '',
  });
  const [orderIdFilter, setOrderIdFilter] = useState('');
  const [invoiceFilter, setInvoiceFilter] = useState('');
  const [buyerEmailFilter, setBuyerEmailFilter] = useState('');
  const [orderStatusFilter, setOrderStatusFilter] = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [dateFieldFilter, setDateFieldFilter] = useState('updated_at');
  const [fromDateFilter, setFromDateFilter] = useState('');
  const [toDateFilter, setToDateFilter] = useState('');

  const formatIndianAmount = (value) => {
    const numericValue = Number(value || 0);
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numericValue);
  };

  const formatISTDateTime = (value) => {
    if (!value) return '-';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;

    return new Intl.DateTimeFormat('en-IN', {
      timeZone: 'Asia/Kolkata',
      dateStyle: 'medium',
      timeStyle: 'medium',
      hour12: true,
    }).format(parsed);
  };

  const getReturnReasonLabel = (code, otherReason) => {
    const mapping = {
      not_needed_any_more: 'Not needed any more',
      delivered_late: 'Delivered late',
      quality_not_good: 'Quality not good',
      item_not_same_as_described: 'Item not same as described',
      lower_price_elsewhere: 'Lower price elsewhere',
      other: 'Other',
    };
    if (!code) return '-';
    const base = mapping[code] || code;
    if (code === 'other' && otherReason) {
      return `${base}: ${otherReason}`;
    }
    return base;
  };

  const getConditionLabel = (value) => {
    if (!value) return '-';
    const mapping = { good: 'Good', fair: 'Fair', bad: 'Bad' };
    return mapping[value] || value;
  };

  const getRefundRecommendationLabel = (value) => {
    if (!value) return '-';
    const mapping = { refund: 'Refund', no_refund: 'No Refund' };
    return mapping[value] || value;
  };

  const handlePrintInvoice = async (order) => {
    setActionError('');
    const invoiceWindow = window.open('about:blank', '_blank');

    if (!invoiceWindow) {
      setActionError('Allow pop-ups to open the printable invoice.');
      return;
    }

    invoiceWindow.opener = null;
    invoiceWindow.document.write('<p style="font-family:Segoe UI,Tahoma,sans-serif;padding:24px;">Preparing invoice...</p>');
    invoiceWindow.document.close();

    try {
      const response = await axios.get(`/api/orders/${order.id}/invoice`, { responseType: 'text' });
      invoiceWindow.document.open();
      invoiceWindow.document.write(response.data);
      invoiceWindow.document.close();
      invoiceWindow.focus();
    } catch (err) {
      invoiceWindow.close();
      setActionError(err.response?.data?.detail || 'Failed to generate invoice');
    }
  };

  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get('/api/orders');
      setOrders(response.data || []);
    } catch (err) {
      setError('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const getNextStatus = (status) => {
    const normalized = (status || '').toLowerCase();
    if (normalized === 'confirmed') return 'packaging';
    if (normalized === 'packaging') return 'shipped';
    if (normalized === 'shipped') return 'delivered';
    return null;
  };

  const toDateTimeLocalValue = (value) => {
    if (!value) return '';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '';
    const pad = (num) => String(num).padStart(2, '0');
    return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}T${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
  };

  const openShippingDialog = (order, prefill = null) => {
    const source = prefill || order || {};
    setShippingDialog({
      show: true,
      orderId: order?.id || source?.id || '',
      trackingNumber: source?.tracking_number || '',
      courierCompany: source?.courier_company || '',
      dateShipped: toDateTimeLocalValue(source?.date_shipped) || toDateTimeLocalValue(new Date().toISOString()),
    });
  };

  const closeShippingDialog = () => {
    setShippingDialog({
      show: false,
      orderId: '',
      trackingNumber: '',
      courierCompany: '',
      dateShipped: '',
    });
  };

  const openDeliveryDialog = (order) => {
    setDeliveryDialog({
      show: true,
      orderId: order?.id || '',
      deliveryDate: toDateTimeLocalValue(order?.delivery_date) || toDateTimeLocalValue(new Date().toISOString()),
      deliveredTo: order?.delivered_to || order?.shipping_contact_name || '',
    });
  };

  const closeDeliveryDialog = () => {
    setDeliveryDialog({
      show: false,
      orderId: '',
      deliveryDate: '',
      deliveredTo: '',
    });
  };

  const openReturnPickupDialog = (order) => {
    setReturnPickupDialog({
      show: true,
      order,
      pickupDate: toDateTimeLocalValue(new Date().toISOString()),
      pickupNote: '',
      itemChecks: {},
    });
  };

  const closeReturnPickupDialog = () => {
    setReturnPickupDialog({
      show: false,
      order: null,
      pickupDate: '',
      pickupNote: '',
      itemChecks: {},
    });
  };

  const handleSaveReturnPickup = async () => {
    const order = returnPickupDialog.order;
    if (!order?.id || !returnPickupDialog.pickupDate) {
      setActionError('Pickup date is required.');
      return;
    }

    const returnItems = (order.items || []).filter((item) => item.status === 'return_requested');
    const payloadItems = returnItems.map((item) => ({
      item_id: item.id,
      confirmed: Boolean(returnPickupDialog.itemChecks[item.id]),
    }));

    setActionError('');
    setActionSuccess('');
    setActionLoading(order.id);
    try {
      await axios.post(`/api/orders/${order.id}/return-pickup`, {
        pickup_date: new Date(returnPickupDialog.pickupDate).toISOString(),
        pickup_note: returnPickupDialog.pickupNote.trim() || null,
        items: payloadItems,
      });
      setActionSuccess(`Return pickup captured for Order #${order.id.slice(0, 8)}. Buyer notified by email.`);
      closeReturnPickupDialog();
      fetchOrders();
    } catch (err) {
      setActionError(err.response?.data?.detail || 'Failed to capture return pickup details');
    } finally {
      setActionLoading('');
    }
  };

  const openReturnReceivedDialog = (order) => {
    const returnItems = (order.items || []).filter((item) => ['return_picked_up', 'return_requested', 'return_received'].includes(item.status));
    const itemRows = returnItems.map((item) => ({
      itemId: item.id,
      received: item.return_received_confirmed ?? true,
      condition: item.return_condition || 'good',
      recommendation: item.return_refund_recommendation || 'refund',
    }));

    setReturnReceivedDialog({
      show: true,
      order,
      receivedDate: toDateTimeLocalValue(order?.return_received_date) || toDateTimeLocalValue(new Date().toISOString()),
      note: order?.return_received_note || '',
      orderRecommendation: order?.return_refund_recommendation || 'refund',
      itemRows,
    });
  };

  const closeReturnReceivedDialog = () => {
    setReturnReceivedDialog({
      show: false,
      order: null,
      receivedDate: '',
      note: '',
      orderRecommendation: 'refund',
      itemRows: [],
    });
  };

  const handleSaveReturnReceived = async () => {
    const order = returnReceivedDialog.order;
    if (!order?.id || !returnReceivedDialog.receivedDate) {
      setActionError('Received date is required.');
      return;
    }

    if (!['refund', 'no_refund'].includes(returnReceivedDialog.orderRecommendation)) {
      setActionError('Choose refund recommendation for this return.');
      return;
    }

    setActionError('');
    setActionSuccess('');
    setActionLoading(order.id);
    try {
      await axios.post(`/api/orders/${order.id}/return-received`, {
        received_date: new Date(returnReceivedDialog.receivedDate).toISOString(),
        note: returnReceivedDialog.note.trim() || null,
        refund_recommendation: returnReceivedDialog.orderRecommendation,
        items: returnReceivedDialog.itemRows.map((row) => ({
          item_id: row.itemId,
          received: Boolean(row.received),
          condition: row.received ? row.condition : null,
          refund_recommendation: row.received ? row.recommendation : null,
        })),
      });
      if (returnReceivedDialog.orderRecommendation === 'refund') {
        setActionSuccess(`Return received captured for Order #${order.id.slice(0, 8)}. Order cancelled with refund.`);
      } else {
        setActionSuccess(`Return received captured for Order #${order.id.slice(0, 8)}. Order cancelled with refund disapproved.`);
      }
      closeReturnReceivedDialog();
      fetchOrders();
    } catch (err) {
      setActionError(err.response?.data?.detail || 'Failed to capture return received details');
    } finally {
      setActionLoading('');
    }
  };

  const handleAdvanceStatus = async (order) => {
    const nextStatus = getNextStatus(order.status);
    if (!nextStatus) return;
    if (nextStatus === 'delivered') {
      openDeliveryDialog(order);
      return;
    }
    setActionError('');
    setActionSuccess('');
    setActionLoading(order.id);
    try {
      const { data } = await axios.patch(`/api/orders/${order.id}/status`, { status: nextStatus });
      setActionSuccess(`Order #${order.id.slice(0, 8)} moved to ${nextStatus}.`);
      fetchOrders();
      if (nextStatus === 'shipped') {
        openShippingDialog(order, data);
      }
    } catch (err) {
      setActionError(err.response?.data?.detail || 'Failed to update order status');
    } finally {
      setActionLoading('');
    }
  };

  const handleSaveDeliveryDetails = async () => {
    const orderId = deliveryDialog.orderId;
    const deliveryDate = deliveryDialog.deliveryDate;
    const deliveredTo = deliveryDialog.deliveredTo.trim();

    if (!orderId || !deliveryDate || !deliveredTo) {
      setActionError('Delivery date and delivered_to are required.');
      return;
    }

    setActionError('');
    setActionSuccess('');
    setActionLoading(orderId);
    try {
      await axios.patch(`/api/orders/${orderId}/status`, {
        status: 'delivered',
        delivery_date: new Date(deliveryDate).toISOString(),
        delivered_to: deliveredTo,
      });
      setActionSuccess(`Order #${orderId.slice(0, 8)} moved to delivered with delivery details.`);
      closeDeliveryDialog();
      fetchOrders();
    } catch (err) {
      setActionError(err.response?.data?.detail || 'Failed to mark order as delivered');
    } finally {
      setActionLoading('');
    }
  };

  const handlePrintReturnSlip = async (order) => {
    const returnItems = (order.items || []).filter((item) => item.status === 'return_requested');
    if (returnItems.length === 0) {
      setActionError('No return-requested items found for this order.');
      return;
    }

    const popup = window.open('', '_blank');
    if (!popup) {
      setActionError('Allow pop-ups to print return slips.');
      return;
    }

    try {
      // Fetch store settings to get store address
      const storeResponse = await axios.get('/api/store-settings');
      const storeSettings = storeResponse.data;

      const rows = returnItems
        .map((item, index) => `
          <tr>
            <td>${index + 1}</td>
            <td>${item.product_name || item.product_id || '-'}</td>
            <td>${item.variant_sku || 'Base product'}</td>
            <td>${item.quantity || 0}</td>
            <td>${getReturnReasonLabel(item.return_reason_code, item.return_reason_other)}</td>
          </tr>
        `)
        .join('');

      const storeAddressLines = [];
      if (storeSettings.store_name) storeAddressLines.push(storeSettings.store_name);
      if (storeSettings.store_addr1) storeAddressLines.push(storeSettings.store_addr1);
      if (storeSettings.store_addr2) storeAddressLines.push(storeSettings.store_addr2);
      
      const cityState = [storeSettings.store_city, storeSettings.store_state]
        .filter(Boolean)
        .join(', ');
      if (cityState) storeAddressLines.push(cityState);
      
      const countryPin = [storeSettings.store_country, storeSettings.store_pin]
        .filter(Boolean)
        .join(' - ');
      if (countryPin) storeAddressLines.push(countryPin);

      const storeAddressHTML = storeAddressLines
        .map(line => `<div>${line}</div>`)
        .join('');

      popup.document.write(`
        <html>
          <head>
            <title>Return Mailing Slip - ${order.id}</title>
            <style>
              @page {
                size: 4in 6in;
                margin: 0.25in;
              }
              
              * {
                box-sizing: border-box;
              }
              
              body {
                font-family: Arial, sans-serif;
                padding: 0.15in;
                margin: 0;
                color: #212529;
                font-size: 11px;
                line-height: 1.3;
              }
              
              h1 {
                font-size: 14px;
                margin: 0 0 6px 0;
                font-weight: bold;
              }
              
              h2 {
                font-size: 11px;
                margin: 4px 0 4px 0;
                font-weight: bold;
                text-decoration: underline;
              }
              
              .section {
                border: 1px solid #000;
                padding: 6px;
                margin-bottom: 6px;
                page-break-inside: avoid;
              }
              
              table {
                width: 100%;
                border-collapse: collapse;
                font-size: 9px;
              }
              
              th {
                border: 1px solid #000;
                padding: 3px;
                text-align: left;
                font-weight: bold;
                font-size: 9px;
              }
              
              td {
                border: 1px solid #000;
                padding: 3px;
                text-align: left;
                vertical-align: top;
                font-size: 9px;
              }
              
              .muted {
                font-size: 8px;
                color: #666;
              }
              
              div {
                margin: 2px 0;
                word-wrap: break-word;
                overflow-wrap: break-word;
              }
              
              strong {
                font-weight: bold;
              }
              
              @media print {
                body {
                  padding: 0.15in;
                  margin: 0;
                }
              }
            </style>
          </head>
          <body>
            <h1>Return Mailing Slip</h1>
            <div class="section">
              <h2>Order Details</h2>
              <div><strong>Order ID:</strong> ${order.id}</div>
              <div><strong>Invoice:</strong> ${order.invoice_number || '-'}</div>
              <div><strong>Buyer:</strong> ${order.user_name || '-'} (${order.user_email || '-'})</div>
            </div>
            <div class="section">
              <h2>Return Pickup Address (Buyer)</h2>
              <div>${order.shipping_contact_name || '-'}</div>
              <div>${order.shipping_contact_isd_code || ''} ${order.shipping_contact_number || ''}</div>
              <div>${order.shipping_addr_line1 || '-'}</div>
              <div>${order.shipping_addr_line2 || ''}</div>
              <div>${order.shipping_city || '-'}, ${order.shipping_state || '-'}</div>
              <div>${order.shipping_country || '-'} - ${order.shipping_pincode || '-'}</div>
            </div>
            <div class="section">
              <h2>Deliver To (Store Address)</h2>
              ${storeAddressHTML}
              <div><strong>Email:</strong> ${storeSettings.contact_email || '-'}</div>
              <div><strong>Phone:</strong> ${storeSettings.contact_phone_country ? '+' + storeSettings.contact_phone_country : ''} ${storeSettings.contact_phone || '-'}</div>
            </div>
            <div class="section">
              <h2>Return Items</h2>
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Product</th>
                    <th>Variant</th>
                    <th>Qty</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>${rows}</tbody>
              </table>
              <p class="muted">Please attach this slip with return shipment labels.</p>
            </div>
            <script>window.print();</script>
          </body>
        </html>
      `);
      popup.document.close();
    } catch (err) {
      setActionError(err.response?.data?.detail || 'Failed to fetch store address for return slip');
    }
  };

  const handleCreateCourierAccessLink = async (order) => {
    setActionError('');
    setActionSuccess('');
    setActionLoading(order.id);

    try {
      const response = await axios.post(`/api/orders/${order.id}/courier-access`);
      console.log('Courier access link response:', response.data.access_url);
      const returnedAccessUrl = response.data?.access_url || '';
      const accessUrl = /^https?:\/\//i.test(returnedAccessUrl)
        ? returnedAccessUrl
        : `${window.location.origin}${returnedAccessUrl}`;

      let clipboardOk = false;
      if (navigator.clipboard?.writeText) {
        try {
          await navigator.clipboard.writeText(accessUrl);
          clipboardOk = true;
        } catch {
          clipboardOk = false;
        }
      }

      setCourierLinkInfo({ orderId: order.id, url: accessUrl, clipboardOk });
      setActionSuccess(`Courier access link generated for Order #${order.id.slice(0, 8)}.${clipboardOk ? ' Also copied to clipboard.' : ''}`);
    } catch (err) {
      setActionError(err.response?.data?.detail || 'Failed to create courier access link');
    } finally {
      setActionLoading('');
    }
  };

  const handleSaveShippingDetails = async () => {
    const orderId = shippingDialog.orderId;
    const trackingNumber = shippingDialog.trackingNumber.trim();
    const courierCompany = shippingDialog.courierCompany.trim();
    const dateShipped = shippingDialog.dateShipped;

    if (!orderId || !trackingNumber || !courierCompany || !dateShipped) {
      setActionError('Tracking number, courier company and date shipped are required.');
      return;
    }

    setActionError('');
    setActionSuccess('');
    setActionLoading(orderId);
    try {
      await axios.patch(`/api/orders/${orderId}/shipping-details`, {
        tracking_number: trackingNumber,
        courier_company: courierCompany,
        date_shipped: new Date(dateShipped).toISOString(),
      });
      setActionSuccess(`Shipping details saved for Order #${orderId.slice(0, 8)}. Buyer notified by email.`);
      closeShippingDialog();
      fetchOrders();
    } catch (err) {
      setActionError(err.response?.data?.detail || 'Failed to save shipping details');
    } finally {
      setActionLoading('');
    }
  };

  const closeCancelDialog = () => {
    setCancelDialog({ show: false, mode: 'order', order: null, item: null, reason: '' });
  };

  const handleCancelOrder = (order) => {
    setCancelDialog({ show: true, mode: 'order', order, item: null, reason: '' });
  };

  const handleCancelItem = (order, item) => {
    setCancelDialog({ show: true, mode: 'item', order, item, reason: '' });
  };

  const executeCancelDialogAction = async () => {
    const reason = cancelDialog.reason.trim();
    const order = cancelDialog.order;
    const item = cancelDialog.item;
    if (!order || !reason) {
      return;
    }

    setActionError('');
    setActionSuccess('');
    const loadingKey = cancelDialog.mode === 'item' && item ? item.id : order.id;
    setActionLoading(loadingKey);
    closeCancelDialog();
    try {
      const payload = cancelDialog.mode === 'item' && item
        ? { reason, item_ids: [item.id] }
        : { reason };
      const { data } = await axios.post(`/api/orders/${order.id}/cancel`, payload);
      if (data?.refund_processed === false) {
        setActionError(
          `${cancelDialog.mode === 'item' ? 'Item' : 'Order'} cancelled, but refund failed: ${data.refund_failed_detail || 'Razorpay refund request failed'}`
        );
      } else if (cancelDialog.mode === 'item') {
        setActionSuccess(`Item cancelled in Order #${order.id.slice(0, 8)}. Refund initiated for paid amount.`);
      } else {
        setActionSuccess(`Order #${order.id.slice(0, 8)} cancelled. Refund initiated for paid amounts.`);
      }
      fetchOrders();
    } catch (err) {
      setActionError(err.response?.data?.detail || `Failed to cancel ${cancelDialog.mode === 'item' ? 'item' : 'order'}`);
    } finally {
      setActionLoading('');
    }
  };

  const hasFailedRefund = (order) =>
    order.payment_status === 'refund_failed' || (order.items || []).some((item) => item.refund_status === 'failed');

  const handleRetryRefund = async (order, item = null) => {
    setActionError('');
    setActionSuccess('');
    const loadingKey = item ? item.id : order.id;
    setActionLoading(loadingKey);

    try {
      const payload = item ? { item_ids: [item.id] } : {};
      const { data } = await axios.post(`/api/orders/${order.id}/retry-refund`, payload);
      setActionSuccess(
        `Refund retry successful for Order #${order.id.slice(0, 8)}. Refunded ${order.currency} ${formatIndianAmount(data?.refund_amount || 0)}.`
      );
      fetchOrders();
    } catch (err) {
      setActionError(err.response?.data?.detail || 'Failed to retry refund');
    } finally {
      setActionLoading('');
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);

      const escapeCsvCell = (value) => {
        const text = value === null || value === undefined ? '' : String(value);
        if (text.includes(',') || text.includes('"') || text.includes('\n')) {
          return `"${text.replace(/"/g, '""')}"`;
        }
        return text;
      };

      const header = [
        'order_id',
        'created_at',
        'status',
        'buyer_name',
        'buyer_email',
        'currency',
        'price_total',
        'cgst_amount',
        'sgst_amount',
        'igst_amount',
        'grand_total',
        'shipping_city',
        'shipping_state',
        'shipping_country',
        'shipping_pincode',
        'item_count',
        'product_name',
        'variant_sku',
        'size',
        'quantity',
        'item_price',
        'item_currency',
      ];

      const csvRows = [header.join(',')];

      filteredOrders.forEach((order) => {
        const commonRow = [
          order.id,
          order.created_at || '',
          order.status || '',
          order.user_name || '',
          order.user_email || '',
          order.currency || '',
          order.price_total || '0.00',
          order.cgst_amount || '0.00',
          order.sgst_amount || '0.00',
          order.igst_amount || '0.00',
          order.total_amount || '0.00',
          order.shipping_city || '',
          order.shipping_state || '',
          order.shipping_country || '',
          order.shipping_pincode || '',
          (order.items || []).length,
        ];

        if (!order.items || order.items.length === 0) {
          csvRows.push([...commonRow, '', '', '', '', '', ''].map(escapeCsvCell).join(','));
          return;
        }

        order.items.forEach((item) => {
          csvRows.push(
            [
              ...commonRow,
              item.product_name || item.product_id || '',
              item.variant_sku || 'Base product',
              item.size_name || '-',
              item.quantity || 0,
              item.subtotal || item.price || '0.00',
              item.currency || order.currency || '',
            ]
              .map(escapeCsvCell)
              .join(',')
          );
        });
      });

      const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'orders-filtered-export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to export orders');
    } finally {
      setExporting(false);
    }
  };

  const handleViewOrderTransactions = async (order) => {
    setShowTxModal(true);
    setTxRows([]);
    setTxError('');
    setTxLoading(true);
    setTxOrderLabel(`#${order.id}`);

    try {
      const response = await axios.get(`/api/orders/${order.id}/payment-transactions`);
      const rows = Array.isArray(response.data) ? response.data : [];
      rows.sort((a, b) => {
        const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
        const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;
        return bTime - aTime;
      });
      setTxRows(rows);
    } catch (err) {
      setTxError(err.response?.data?.detail || 'Failed to load payment transactions');
    } finally {
      setTxLoading(false);
    }
  };

  const filteredOrders = useMemo(() => {
    const normalizedOrderIdFilter = orderIdFilter.trim().toLowerCase();
    const normalizedInvoiceFilter = invoiceFilter.trim().toLowerCase();
    const normalizedBuyerEmailFilter = buyerEmailFilter.trim().toLowerCase();
    const normalizedOrderStatusFilter = orderStatusFilter.trim().toLowerCase();
    const normalizedPaymentStatusFilter = paymentStatusFilter.trim().toLowerCase();
    const normalizedSourceFilter = sourceFilter.trim().toLowerCase();
    const fromDate = fromDateFilter ? new Date(`${fromDateFilter}T00:00:00`) : null;
    const toDate = toDateFilter ? new Date(`${toDateFilter}T23:59:59.999`) : null;
    const normalizeOrderSource = (source) => {
      const normalizedSource = (source || 'online').trim().toLowerCase();
      return ['pos', 'offline', 'offline_pos', 'offline (pos)'].includes(normalizedSource)
        ? 'pos'
        : 'online';
    };

    return (orders || [])
      .filter((order) => {
        const orderId = (order.id || '').toLowerCase();
        const invoiceNumber = (order.invoice_number || '').toLowerCase();
        const buyerEmail = (order.user_email || '').toLowerCase();
        const orderStatus = (order.status || '').toLowerCase();
        const paymentStatus = (order.payment_status || '').toLowerCase();
        const orderSource = normalizeOrderSource(order.source);
        const orderDate = order[dateFieldFilter] ? new Date(order[dateFieldFilter]) : null;

        if (normalizedOrderIdFilter && !orderId.includes(normalizedOrderIdFilter)) {
          return false;
        }

        if (normalizedInvoiceFilter && !invoiceNumber.includes(normalizedInvoiceFilter)) {
          return false;
        }

        if (normalizedBuyerEmailFilter && !buyerEmail.includes(normalizedBuyerEmailFilter)) {
          return false;
        }

        if (normalizedOrderStatusFilter && orderStatus !== normalizedOrderStatusFilter) {
          return false;
        }

        if (normalizedPaymentStatusFilter && paymentStatus !== normalizedPaymentStatusFilter) {
          return false;
        }

        if (normalizedSourceFilter && orderSource !== normalizedSourceFilter) {
          return false;
        }

        if (fromDate && (!orderDate || Number.isNaN(orderDate.getTime()) || orderDate < fromDate)) {
          return false;
        }

        if (toDate && (!orderDate || Number.isNaN(orderDate.getTime()) || orderDate > toDate)) {
          return false;
        }

        return true;
      })
      .sort((a, b) => {
        const aTime = a.updated_at ? new Date(a.updated_at).getTime() : 0;
        const bTime = b.updated_at ? new Date(b.updated_at).getTime() : 0;
        return bTime - aTime;
      });
  }, [orders, orderIdFilter, invoiceFilter, buyerEmailFilter, orderStatusFilter, paymentStatusFilter, sourceFilter, dateFieldFilter, fromDateFilter, toDateFilter]);

  const orderStatusOptions = useMemo(
    () => [...new Set((orders || []).map((order) => (order.status || '').toLowerCase()).filter(Boolean))].sort(),
    [orders]
  );

  const paymentStatusOptions = useMemo(
    () => [...new Set((orders || []).map((order) => (order.payment_status || '').toLowerCase()).filter(Boolean))].sort(),
    [orders]
  );

  const handleClearFilters = () => {
    setOrderIdFilter('');
    setInvoiceFilter('');
    setBuyerEmailFilter('');
    setOrderStatusFilter('');
    setPaymentStatusFilter('');
    setSourceFilter('');
    setDateFieldFilter('updated_at');
    setFromDateFilter('');
    setToDateFilter('');
  };

  const isCancelableOrderStatus = (status) => ['confirmed', 'packaging'].includes((status || '').toLowerCase());

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading orders...</span>
        </Spinner>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <Row className="mb-4 align-items-center g-3">
        <Col>
          <h1>Orders</h1>
          <p className="text-muted mb-0">Review buyer orders and export the persisted purchase totals.</p>
        </Col>
        <Col xs="auto">
          <Button onClick={handleExport} disabled={exporting || filteredOrders.length === 0}>
            {exporting ? 'Exporting...' : 'Export CSV'}
          </Button>
        </Col>
      </Row>

      {error && <Alert variant="danger">{error}</Alert>}
      {actionError && <Alert variant="danger" dismissible onClose={() => setActionError('')}>{actionError}</Alert>}
      {actionSuccess && <Alert variant="success" dismissible onClose={() => { setActionSuccess(''); setCourierLinkInfo(null); }}>{actionSuccess}</Alert>}
      {courierLinkInfo && (
        <Alert variant="info" dismissible onClose={() => setCourierLinkInfo(null)}>
          <strong>Courier Link for Order #{courierLinkInfo.orderId.slice(0, 8)}</strong>
          <div className="mt-1" style={{ wordBreak: 'break-all', fontFamily: 'monospace', fontSize: '0.8rem', background: '#f8f9fa', padding: '6px', borderRadius: '4px', userSelect: 'all' }}>
            {courierLinkInfo.url}
          </div>
          <div className="mt-1 text-muted" style={{ fontSize: '0.75rem' }}>
            {courierLinkInfo.clipboardOk ? 'Copied to clipboard. You can also select the link above.' : 'Clipboard unavailable (HTTP). Select and copy the link above manually.'}
          </div>
        </Alert>
      )}

      {orders.length > 0 && (
        <Card className="mb-3">
          <Card.Body>
            <Row className="g-3 align-items-end">
              <Col xs={12} md={3}>
                <label className="form-label mb-1">Order ID</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by order ID"
                  value={orderIdFilter}
                  onChange={(event) => setOrderIdFilter(event.target.value)}
                />
              </Col>
              <Col xs={12} md={3}>
                <label className="form-label mb-1">Invoice Number</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by invoice number"
                  value={invoiceFilter}
                  onChange={(event) => setInvoiceFilter(event.target.value)}
                />
              </Col>
              <Col xs={12} md={2}>
                <label className="form-label mb-1">Buyer Email ID</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by buyer email"
                  value={buyerEmailFilter}
                  onChange={(event) => setBuyerEmailFilter(event.target.value)}
                />
              </Col>
              <Col xs={12} md={2}>
                <label className="form-label mb-1">Order Status</label>
                <select
                  className="form-select"
                  value={orderStatusFilter}
                  onChange={(event) => setOrderStatusFilter(event.target.value)}
                >
                  <option value="">All</option>
                  {orderStatusOptions.map((statusOption) => (
                    <option key={statusOption} value={statusOption}>{statusOption}</option>
                  ))}
                </select>
              </Col>
              <Col xs={12} md={2}>
                <label className="form-label mb-1">Payment Status</label>
                <select
                  className="form-select"
                  value={paymentStatusFilter}
                  onChange={(event) => setPaymentStatusFilter(event.target.value)}
                >
                  <option value="">All</option>
                  {paymentStatusOptions.map((statusOption) => (
                    <option key={statusOption} value={statusOption}>{statusOption}</option>
                  ))}
                </select>
              </Col>
              <Col xs={12} md={2}>
                <label className="form-label mb-1">Source</label>
                <select
                  className="form-select"
                  value={sourceFilter}
                  onChange={(event) => setSourceFilter(event.target.value)}
                >
                  <option value="">All</option>
                  <option value="online">Online</option>
                  <option value="pos">Offline (POS)</option>
                </select>
              </Col>
              <Col xs={12} md={2}>
                <label className="form-label mb-1">Date applies to</label>
                <select
                  className="form-select"
                  value={dateFieldFilter}
                  onChange={(event) => setDateFieldFilter(event.target.value)}
                >
                  <option value="updated_at">Modified date</option>
                  <option value="created_at">Created date</option>
                </select>
              </Col>
              <Col xs={12} md={2}>
                <label className="form-label mb-1">From date</label>
                <input
                  type="date"
                  className="form-control"
                  value={fromDateFilter}
                  onChange={(event) => setFromDateFilter(event.target.value)}
                />
              </Col>
              <Col xs={12} md={2}>
                <label className="form-label mb-1">To date</label>
                <input
                  type="date"
                  className="form-control"
                  value={toDateFilter}
                  onChange={(event) => setToDateFilter(event.target.value)}
                />
              </Col>
              <Col xs={12} md={2} className="d-grid">
                <Button variant="outline-secondary" onClick={handleClearFilters}>
                  Clear
                </Button>
              </Col>
              <Col xs={12}>
                <div className="small text-muted">
                  Showing {filteredOrders.length} of {orders.length} orders (sorted by last update, newest to oldest)
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {orders.length === 0 ? (
        <Card>
          <Card.Body className="text-center py-5">
            <h5>No orders found</h5>
            <p className="text-muted mb-0">Orders will appear here once buyers complete purchases.</p>
          </Card.Body>
        </Card>
      ) : filteredOrders.length === 0 ? (
        <Card>
          <Card.Body className="text-center py-5">
            <h5>No results for current filters</h5>
            <p className="text-muted mb-0">Try changing Order ID, Invoice Number, or Buyer Email filters.</p>
          </Card.Body>
        </Card>
      ) : (
        <Row className="g-4">
          {filteredOrders.map((order) => (
            <Col xs={12} key={order.id}>
              <Card>
                <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <div>
                    <div className="fw-bold">Order #{order.id}</div>
                    <small className="text-muted d-block">Invoice #{order.invoice_number || '-'}</small>
                    <small className="text-muted">
                      Placed on {formatISTDateTime(order.created_at)} IST by {order.user_name || 'Unknown Buyer'}
                    </small>
                  </div>
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <Badge bg={(order.source || 'online') === 'pos' ? 'dark' : 'light'} text={(order.source || 'online') === 'pos' ? 'light' : 'dark'}>
                      {(order.source || 'online') === 'pos' ? 'POS' : 'Online'}
                    </Badge>
                    <Badge bg="secondary">{order.status}</Badge>
                    <Badge bg={order.payment_status === 'paid' ? 'success' : order.payment_status === 'payment_failed' ? 'danger' : order.payment_status?.includes('refund') ? 'info' : order.payment_status === 'cancelled' ? 'secondary' : 'warning'}>
                      {order.payment_status || 'payment_pending'}
                    </Badge>
                    <span className="text-muted">{order.user_email || 'No email'}</span>
                    <span className="fw-semibold">{order.currency} {formatIndianAmount(order.total_amount)}</span>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => handlePrintInvoice(order)}
                    >
                      Print Invoice
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-dark"
                      onClick={() => handleViewOrderTransactions(order)}
                    >
                      View Payment Transactions
                    </Button>
                    {getNextStatus(order.status) && (
                      <Button
                        size="sm"
                        variant="outline-primary"
                        disabled={actionLoading === order.id}
                        onClick={() => handleAdvanceStatus(order)}
                      >
                        {actionLoading === order.id ? <Spinner size="sm" animation="border" /> : `Move to ${getNextStatus(order.status)}`}
                      </Button>
                    )}
                    {['shipped', 'delivered'].includes((order.status || '').toLowerCase()) && (
                      <Button
                        size="sm"
                        variant="outline-success"
                        disabled={actionLoading === order.id}
                        onClick={() => openShippingDialog(order)}
                      >
                        {order.tracking_number ? 'Update Shipping Details' : 'Add Shipping Details'}
                      </Button>
                    )}
                    {order.status !== 'cancelled' && (
                      <Button
                        size="sm"
                        variant="outline-danger"
                        disabled={actionLoading === order.id || !isCancelableOrderStatus(order.status)}
                        onClick={() => handleCancelOrder(order)}
                      >
                        Cancel Order
                      </Button>
                    )}
                    {(order.items || []).some((item) => item.status === 'return_requested') && (
                      <Button
                        size="sm"
                        variant="outline-info"
                        onClick={() => handlePrintReturnSlip(order)}
                      >
                        Print Return Slip
                      </Button>
                    )}
                    {(order.items || []).some((item) => item.status === 'return_requested') && (
                      <Button
                        size="sm"
                        variant="outline-primary"
                        disabled={actionLoading === order.id}
                        onClick={() => handleCreateCourierAccessLink(order)}
                      >
                        {actionLoading === order.id ? <Spinner size="sm" animation="border" /> : 'Courier Access Link'}
                      </Button>
                    )}
                    {(order.status || '').toLowerCase() === 'return_requested' && (
                      <Button
                        size="sm"
                        variant="outline-warning"
                        disabled={actionLoading === order.id}
                        onClick={() => openReturnPickupDialog(order)}
                      >
                        Capture Return Pickup
                      </Button>
                    )}
                    {['return_picked_up', 'return_requested', 'return_received'].includes((order.status || '').toLowerCase()) && (
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        disabled={actionLoading === order.id}
                        onClick={() => openReturnReceivedDialog(order)}
                      >
                        Capture Return Received
                      </Button>
                    )}
                    {hasFailedRefund(order) && (
                      <Button
                        size="sm"
                        variant="outline-warning"
                        disabled={actionLoading === order.id}
                        onClick={() => handleRetryRefund(order)}
                      >
                        {actionLoading === order.id ? <Spinner size="sm" animation="border" /> : 'Retry Refund'}
                      </Button>
                    )}
                  </div>
                </Card.Header>
                <Card.Body>
                  <Row className="g-4">
                    <Col xl={8}>
                      <h6 className="mb-3">Items</h6>
                      <Table responsive size="sm" className="mb-0">
                        <thead>
                          <tr>
                            <th>Product</th>
                            <th>Variant SKU</th>
                            <th>Size</th>
                            <th>Qty</th>
                            <th>Price</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {order.items.map((item) => (
                            <tr key={item.id}>
                              <td>
                                <div className="d-flex align-items-center gap-2">
                                  {item.thumbnail_url ? (
                                    <img
                                      src={item.thumbnail_url}
                                      alt={item.product_name || item.product_id}
                                      style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }}
                                    />
                                  ) : (
                                    <div style={{ width: 40, height: 40, background: '#e9ecef', borderRadius: 4, flexShrink: 0 }} />
                                  )}
                                  <span>{item.product_name || item.product_id}</span>
                                </div>
                              </td>
                              <td>{item.variant_sku || 'Base product'}</td>
                              <td>{item.size_name || '-'}</td>
                              <td>{item.quantity}</td>
                              <td>{item.currency} {formatIndianAmount(item.subtotal ?? ((Number(item.price) || 0) * (item.quantity || 0)))}</td>
                              <td>
                                {item.status === 'cancelled' ? (
                                  <div className="d-flex align-items-center gap-2">
                                    <Badge bg="secondary">cancelled</Badge>
                                    {item.refund_status === 'failed' && (
                                      <Button
                                        size="sm"
                                        variant="outline-warning"
                                        disabled={actionLoading === item.id}
                                        onClick={() => handleRetryRefund(order, item)}
                                      >
                                        {actionLoading === item.id ? <Spinner size="sm" animation="border" /> : 'Retry Refund'}
                                      </Button>
                                    )}
                                  </div>
                                ) : item.status === 'return_requested' ? (
                                  <div className="d-flex flex-column gap-1">
                                    <Badge bg="warning" text="dark">return requested</Badge>
                                    <small className="text-muted">{getReturnReasonLabel(item.return_reason_code, item.return_reason_other)}</small>
                                  </div>
                                ) : item.status === 'return_picked_up' ? (
                                  <div className="d-flex flex-column gap-1">
                                    <Badge bg="info">return picked up</Badge>
                                    <small className="text-muted">Pickup confirmed: {item.return_pickup_confirmed ? 'Yes' : 'No'}</small>
                                  </div>
                                ) : item.status === 'return_received' ? (
                                  <div className="d-flex flex-column gap-1">
                                    <Badge bg="secondary">return received</Badge>
                                    <small className="text-muted">Condition: {getConditionLabel(item.return_condition)}</small>
                                    <small className="text-muted">Recommendation: {getRefundRecommendationLabel(item.return_refund_recommendation)}</small>
                                  </div>
                                ) : (
                                  <Button
                                    size="sm"
                                    variant="outline-danger"
                                    disabled={actionLoading === item.id || !isCancelableOrderStatus(order.status)}
                                    onClick={() => handleCancelItem(order, item)}
                                  >
                                    {actionLoading === item.id ? <Spinner size="sm" animation="border" /> : 'Cancel Item'}
                                  </Button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Col>
                    <Col xl={4}>
                      <Tabs
                        defaultActiveKey="summary"
                        id={`order-tabs-${order.id}`}
                        className="mb-3"
                      >
                        <Tab eventKey="summary" title="Purchase Summary">
                          <div className="border rounded p-3 bg-light-subtle">
                            <div className="d-flex justify-content-between py-1">
                              <span>Price Total</span>
                              <span>{order.currency} {formatIndianAmount(order.price_total)}</span>
                            </div>
                            <div className="d-flex justify-content-between py-1">
                              <span>CGST</span>
                              <span>{order.currency} {formatIndianAmount(order.cgst_amount)}</span>
                            </div>
                            <div className="d-flex justify-content-between py-1">
                              <span>SGST</span>
                              <span>{order.currency} {formatIndianAmount(order.sgst_amount)}</span>
                            </div>
                            <div className="d-flex justify-content-between py-1">
                              <span>IGST</span>
                              <span>{order.currency} {formatIndianAmount(order.igst_amount)}</span>
                            </div>
                            <div className="d-flex justify-content-between pt-2 mt-2 border-top fw-semibold text-primary">
                              <span>Grand Total</span>
                              <span>{order.currency} {formatIndianAmount(order.total_amount)}</span>
                            </div>

                            <div className="d-flex justify-content-between pt-2 mt-2 border-top">
                              <span>Refund Status</span>
                              <span className="text-capitalize">{(order.refund_status || 'not_required').replace('_', ' ')}</span>
                            </div>
                            <div className="d-flex justify-content-between py-1">
                              <span>Refunded Amount</span>
                              <span>{order.currency} {formatIndianAmount(order.refunded_amount || 0)}</span>
                            </div>
                            <div className="d-flex justify-content-between py-1">
                              <span>Refunded At</span>
                              <span>{formatISTDateTime(order.refunded_at)}</span>
                            </div>
                          </div>
                        </Tab>
                        <Tab eventKey="delivery" title="Delivery Snapshot">
                          <div className="border rounded p-3 bg-light-subtle">
                            <div className="fw-semibold">{order.shipping_contact_name || '-'}</div>
                            <div>{order.shipping_contact_isd_code || ''} {order.shipping_contact_number || ''}</div>
                            <div>{order.shipping_addr_line1}</div>
                            {order.shipping_addr_line2 && <div>{order.shipping_addr_line2}</div>}
                            <div>{order.shipping_city}, {order.shipping_state}</div>
                            <div>{order.shipping_country} - {order.shipping_pincode}</div>
                            <hr />
                            <div><strong>Courier:</strong> {order.courier_company || '-'}</div>
                            <div><strong>Tracking #:</strong> {order.tracking_number || '-'}</div>
                            <div><strong>Date Shipped:</strong> {formatISTDateTime(order.date_shipped)}</div>
                            <div><strong>Delivery Date:</strong> {formatISTDateTime(order.delivery_date)}</div>
                            <div><strong>Delivered To:</strong> {order.delivered_to || '-'}</div>
                          </div>
                        </Tab>
                        <Tab eventKey="returns" title="Return Snapshot">
                          <div className="border rounded p-3 bg-light-subtle">
                            <div><strong>Return Requested At:</strong> {formatISTDateTime(order.return_requested_at)}</div>
                            <div><strong>Pickup Date:</strong> {formatISTDateTime(order.return_pickup_date)}</div>
                            <div><strong>Pickup Note:</strong> {order.return_pickup_note || '-'}</div>
                            <hr />
                            <div><strong>Received Date:</strong> {formatISTDateTime(order.return_received_date)}</div>
                            <div><strong>Received Note:</strong> {order.return_received_note || '-'}</div>
                            <div><strong>Order Recommendation:</strong> {getRefundRecommendationLabel(order.return_refund_recommendation)}</div>
                            <hr />
                            {(order.items || []).filter((item) => ['return_requested', 'return_picked_up', 'return_received', 'returned', 'cancelled'].includes(item.status) && (item.return_reason_code || item.return_reason_other)).map((item) => (
                              <div key={`return-${item.id}`} className="mb-2 pb-2 border-bottom">
                                <div className="fw-semibold">{item.product_name || item.product_id}</div>
                                <div className="small">Reason: {getReturnReasonLabel(item.return_reason_code, item.return_reason_other)}</div>
                                <div className="small">Picked Up: {item.return_pickup_confirmed ? 'Yes' : 'No'}</div>
                                <div className="small">Received: {item.return_received_confirmed ? 'Yes' : 'No'}</div>
                                <div className="small">Condition: {getConditionLabel(item.return_condition)}</div>
                                <div className="small">Item Recommendation: {getRefundRecommendationLabel(item.return_refund_recommendation)}</div>
                              </div>
                            ))}
                          </div>
                        </Tab>
                      </Tabs>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      <Modal show={showTxModal} onHide={() => setShowTxModal(false)} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>Payment Transactions {txOrderLabel}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {txLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading payment transactions...</span>
              </Spinner>
            </div>
          ) : txError ? (
            <Alert variant="danger" className="mb-0">{txError}</Alert>
          ) : txRows.length === 0 ? (
            <div className="text-muted">No payment transactions found for this order.</div>
          ) : (
            <Table responsive hover size="sm" className="mb-0">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Event</th>
                  <th>Status</th>
                  <th>Amount</th>
                  <th>Razorpay IDs</th>
                  <th>Error</th>
                </tr>
              </thead>
              <tbody>
                {txRows.map((tx) => (
                  <tr key={tx.id}>
                    <td>{formatISTDateTime(tx.created_at)}</td>
                    <td>
                      <div className="text-capitalize">{tx.event_type || '-'}</div>
                      <div className="text-muted small">{tx.provider || '-'}</div>
                    </td>
                    <td>
                      <Badge bg={tx.status === 'failed' ? 'danger' : tx.status === 'processed' || tx.status === 'paid' ? 'success' : 'secondary'}>
                        {tx.status || '-'}
                      </Badge>
                    </td>
                    <td>{tx.currency} {formatIndianAmount(tx.amount)}</td>
                    <td>
                      <div className="small">Order: {tx.razorpay_order_id || '-'}</div>
                      <div className="small">Payment: {tx.razorpay_payment_id || '-'}</div>
                      <div className="small">Refund: {tx.razorpay_refund_id || '-'}</div>
                    </td>
                    <td>
                      {tx.error_message ? <span className="text-danger small">{tx.error_message}</span> : <span className="text-muted small">-</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Modal.Body>
      </Modal>

      <Modal show={cancelDialog.show} onHide={closeCancelDialog} centered>
        <Modal.Header closeButton>
          <Modal.Title>{cancelDialog.mode === 'item' ? 'Cancel Order Item' : 'Cancel Order'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">Enter cancellation reason (required):</p>
          <textarea
            className="form-control"
            rows={3}
            value={cancelDialog.reason}
            onChange={(event) => setCancelDialog((current) => ({ ...current, reason: event.target.value }))}
            placeholder="Type cancellation reason"
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeCancelDialog}>Cancel</Button>
          <Button
            variant="danger"
            onClick={executeCancelDialogAction}
            disabled={!cancelDialog.reason.trim()}
          >
            {cancelDialog.mode === 'item' ? 'Cancel Item' : 'Cancel Order'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={shippingDialog.show} onHide={closeShippingDialog} centered>
        <Modal.Header closeButton>
          <Modal.Title>Shipping Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-label">Tracking Number</label>
            <input
              type="text"
              className="form-control"
              value={shippingDialog.trackingNumber}
              onChange={(event) => setShippingDialog((current) => ({ ...current, trackingNumber: event.target.value }))}
              placeholder="Enter tracking number"
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Courier Company</label>
            <input
              type="text"
              className="form-control"
              value={shippingDialog.courierCompany}
              onChange={(event) => setShippingDialog((current) => ({ ...current, courierCompany: event.target.value }))}
              placeholder="Enter courier company"
            />
          </div>
          <div>
            <label className="form-label">Date Shipped</label>
            <input
              type="datetime-local"
              className="form-control"
              value={shippingDialog.dateShipped}
              onChange={(event) => setShippingDialog((current) => ({ ...current, dateShipped: event.target.value }))}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeShippingDialog}>Cancel</Button>
          <Button
            variant="success"
            onClick={handleSaveShippingDetails}
            disabled={actionLoading === shippingDialog.orderId}
          >
            {actionLoading === shippingDialog.orderId ? <Spinner size="sm" animation="border" /> : 'Save & Notify Buyer'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={returnPickupDialog.show} onHide={closeReturnPickupDialog} centered>
        <Modal.Header closeButton>
          <Modal.Title>Capture Return Pickup</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-label">Date Picked Up</label>
            <input
              type="datetime-local"
              className="form-control"
              value={returnPickupDialog.pickupDate}
              onChange={(event) => setReturnPickupDialog((current) => ({ ...current, pickupDate: event.target.value }))}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Pickup Note</label>
            <textarea
              className="form-control"
              rows={3}
              value={returnPickupDialog.pickupNote}
              onChange={(event) => setReturnPickupDialog((current) => ({ ...current, pickupNote: event.target.value }))}
              placeholder="Optional pickup note"
            />
          </div>
          <div>
            <label className="form-label">Confirm Picked Up Items</label>
            {(returnPickupDialog.order?.items || []).filter((item) => item.status === 'return_requested').map((item) => (
              <div key={`pickup-${item.id}`} className="form-check mb-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id={`pickup-item-${item.id}`}
                  checked={Boolean(returnPickupDialog.itemChecks[item.id])}
                  onChange={(event) => {
                    const checked = event.target.checked;
                    setReturnPickupDialog((current) => ({
                      ...current,
                      itemChecks: {
                        ...current.itemChecks,
                        [item.id]: checked,
                      },
                    }));
                  }}
                />
                <label className="form-check-label" htmlFor={`pickup-item-${item.id}`}>
                  {item.product_name || item.product_id} ({item.variant_sku || 'Base product'})
                </label>
              </div>
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeReturnPickupDialog}>Cancel</Button>
          <Button
            variant="warning"
            onClick={handleSaveReturnPickup}
            disabled={actionLoading === returnPickupDialog.order?.id}
          >
            {actionLoading === returnPickupDialog.order?.id ? <Spinner size="sm" animation="border" /> : 'Save Pickup & Notify Buyer'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={returnReceivedDialog.show} onHide={closeReturnReceivedDialog} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Capture Return Received</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-label">Date Received</label>
            <input
              type="datetime-local"
              className="form-control"
              value={returnReceivedDialog.receivedDate}
              onChange={(event) => setReturnReceivedDialog((current) => ({ ...current, receivedDate: event.target.value }))}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Order Recommendation</label>
            <select
              className="form-select"
              value={returnReceivedDialog.orderRecommendation}
              onChange={(event) => setReturnReceivedDialog((current) => ({ ...current, orderRecommendation: event.target.value }))}
            >
              <option value="refund">Refund</option>
              <option value="no_refund">No Refund</option>
            </select>
          </div>
          <div className="mb-3">
            <label className="form-label">Return Note</label>
            <textarea
              className="form-control"
              rows={3}
              value={returnReceivedDialog.note}
              onChange={(event) => setReturnReceivedDialog((current) => ({ ...current, note: event.target.value }))}
              placeholder="Optional return received note"
            />
          </div>
          <Table responsive size="sm" className="mb-0">
            <thead>
              <tr>
                <th>Item</th>
                <th>Received</th>
                <th>Condition</th>
                <th>Recommendation</th>
              </tr>
            </thead>
            <tbody>
              {returnReceivedDialog.itemRows.map((row) => {
                const item = (returnReceivedDialog.order?.items || []).find((entry) => entry.id === row.itemId);
                return (
                  <tr key={`received-${row.itemId}`}>
                    <td>{item?.product_name || item?.product_id || row.itemId}</td>
                    <td>
                      <input
                        type="checkbox"
                        checked={Boolean(row.received)}
                        onChange={(event) => {
                          const checked = event.target.checked;
                          setReturnReceivedDialog((current) => ({
                            ...current,
                            itemRows: current.itemRows.map((entry) => (
                              entry.itemId === row.itemId ? { ...entry, received: checked } : entry
                            )),
                          }));
                        }}
                      />
                    </td>
                    <td>
                      <select
                        className="form-select form-select-sm"
                        value={row.condition}
                        disabled={!row.received}
                        onChange={(event) => {
                          const nextValue = event.target.value;
                          setReturnReceivedDialog((current) => ({
                            ...current,
                            itemRows: current.itemRows.map((entry) => (
                              entry.itemId === row.itemId ? { ...entry, condition: nextValue } : entry
                            )),
                          }));
                        }}
                      >
                        <option value="good">Good</option>
                        <option value="fair">Fair</option>
                        <option value="bad">Bad</option>
                      </select>
                    </td>
                    <td>
                      <select
                        className="form-select form-select-sm"
                        value={row.recommendation}
                        disabled={!row.received}
                        onChange={(event) => {
                          const nextValue = event.target.value;
                          setReturnReceivedDialog((current) => ({
                            ...current,
                            itemRows: current.itemRows.map((entry) => (
                              entry.itemId === row.itemId ? { ...entry, recommendation: nextValue } : entry
                            )),
                          }));
                        }}
                      >
                        <option value="refund">Refund</option>
                        <option value="no_refund">No Refund</option>
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeReturnReceivedDialog}>Cancel</Button>
          <Button
            variant="secondary"
            onClick={handleSaveReturnReceived}
            disabled={actionLoading === returnReceivedDialog.order?.id}
          >
            {actionLoading === returnReceivedDialog.order?.id ? <Spinner size="sm" animation="border" /> : 'Save Received & Finalize'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={deliveryDialog.show} onHide={closeDeliveryDialog} centered>
        <Modal.Header closeButton>
          <Modal.Title>Capture Delivery Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-label">Delivery Date</label>
            <input
              type="datetime-local"
              className="form-control"
              value={deliveryDialog.deliveryDate}
              onChange={(event) => setDeliveryDialog((current) => ({ ...current, deliveryDate: event.target.value }))}
            />
          </div>
          <div>
            <label className="form-label">Delivered To</label>
            <input
              type="text"
              className="form-control"
              value={deliveryDialog.deliveredTo}
              onChange={(event) => setDeliveryDialog((current) => ({ ...current, deliveredTo: event.target.value }))}
              placeholder="Name of recipient"
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeDeliveryDialog}>Cancel</Button>
          <Button
            variant="primary"
            onClick={handleSaveDeliveryDetails}
            disabled={actionLoading === deliveryDialog.orderId}
          >
            {actionLoading === deliveryDialog.orderId ? <Spinner size="sm" animation="border" /> : 'Save & Mark Delivered'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AdminOrdersPage;